﻿import { useEffect, useMemo, useRef, useState } from "react";
import { BrandLockup, Button } from "../ui";
import { routes } from "../../data/siteContent";
import { useAuth } from "../../context/AuthContext";
import { getAdminAccessProfile } from "../../services/adminAccessRepository";
import { getUnreadNotificationCount } from "../../services/notificationRepository";
import { appendReturnPath, getReturnPathFromSearch } from "../../routing/accessControl";
import { subscribeUserEconomy } from "../../services/traitCatalogRepository";

const navTones = ["cyan", "green", "purple", "amber", "blue", "pink"];

export default function SiteHeader({ currentPath, currentSearch = "", onNavigate }) {
  const { user, nickname } = useAuth();
  const navRef = useRef(null);
  const [canAccessAdmin, setCanAccessAdmin] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [economy, setEconomy] = useState({ xp: 0, hype: 0, denk: 0 });

  useEffect(() => {
    if (!user?.uid) {
      setEconomy({ xp: 0, hype: 0, denk: 0 });
      return undefined;
    }
    return subscribeUserEconomy(user.uid, setEconomy, () => setEconomy({ xp: 0, hype: 0, denk: 0 }));
  }, [user?.uid]);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!user?.uid) {
        setCanAccessAdmin(false);
        return;
      }
      try {
        const profile = await getAdminAccessProfile(user);
        if (active) setCanAccessAdmin(Boolean(profile?.permissions?.canAccessAdmin));
      } catch {
        if (active) setCanAccessAdmin(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [user]);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!user?.uid) {
        setNotificationCount(0);
        return;
      }
      try {
        const count = await getUnreadNotificationCount({ uid: user.uid });
        if (active) setNotificationCount(count);
      } catch {
        if (active) setNotificationCount(0);
      }
    })();
    return () => {
      active = false;
    };
  }, [user?.uid, currentPath]);

  const navRoutes = useMemo(() => (
    routes.filter((route) => route.path !== "/giris" && (route.path !== "/host" || canAccessAdmin))
  ), [canAccessAdmin]);
  const returnPath = getReturnPathFromSearch(currentSearch);

  const handleNavigate = (event, path) => {
    event.preventDefault();
    onNavigate(path);
  };

  const updateLiquidFocus = (clientX, clientY) => {
    const navElement = navRef.current;
    if (!navElement) return;

    const rect = navElement.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;
    const clampedX = Math.max(0, Math.min(100, x));
    const clampedY = Math.max(0, Math.min(100, y));
    const centerDistance = Math.hypot(clampedX - 50, clampedY - 50);
    const intensity = Math.max(0.38, 1 - centerDistance / 90);

    navElement.style.setProperty("--nav-cx", `${clampedX}%`);
    navElement.style.setProperty("--nav-cy", `${clampedY}%`);
    navElement.style.setProperty("--nav-glow-intensity", intensity.toFixed(3));
  };

  const resetLiquidFocus = () => {
    const navElement = navRef.current;
    if (!navElement) return;
    navElement.style.setProperty("--nav-cx", "50%");
    navElement.style.setProperty("--nav-cy", "50%");
    navElement.style.setProperty("--nav-glow-intensity", "0.54");
  };

  return (
    <header className="site-header">
      <nav
        ref={navRef}
        className="nav-glass nav-glass--liquid"
        aria-label="DenkUP web navigasyonu"
        onMouseMove={(event) => updateLiquidFocus(event.clientX, event.clientY)}
        onMouseEnter={(event) => updateLiquidFocus(event.clientX, event.clientY)}
        onMouseLeave={resetLiquidFocus}
        onTouchMove={(event) => {
          const touch = event.touches?.[0];
          if (!touch) return;
          updateLiquidFocus(touch.clientX, touch.clientY);
        }}
      >
        <span className="nav-liquid nav-liquid--one" aria-hidden="true" />
        <span className="nav-liquid nav-liquid--two" aria-hidden="true" />
        <span className="nav-liquid nav-liquid--three" aria-hidden="true" />

        {user ? (
          <>
            <BrandLockup />
            <div className="nav-links">
              {navRoutes.map((item, index) => (
                <a
                  className={`nav-link-pill nav-tone--${navTones[index % navTones.length]} ${currentPath === item.path ? "is-active" : ""}`}
                  href={item.path}
                  key={item.path}
                  onClick={(event) => handleNavigate(event, item.path)}
                >
                  {item.shortLabel}
                </a>
              ))}
            </div>
            <div className="header-user">
              <div className="header-economy" aria-label="Güncel kullanıcı currency değerleri">
                <span className="header-economy__item header-economy__item--xp" title="XP"><i>XP</i><strong>{economy.xp.toLocaleString("tr-TR")}</strong></span>
                <span className="header-economy__item header-economy__item--hype" title="Hype"><i>HY</i><strong>{economy.hype.toLocaleString("tr-TR")}</strong></span>
                <span className="header-economy__item header-economy__item--denk" title="Denk"><i>DN</i><strong>{economy.denk.toLocaleString("tr-TR")}</strong></span>
              </div>
              <Button className="header-notification-button nav-reflect-button nav-reflect-button--notification" variant="ghost" size="md" onClick={() => onNavigate("/bildirimler")} aria-label="Bildirimleri aç" title="Bildirimler">
                <span className="header-bell-icon" aria-hidden="true" />
                {notificationCount > 0 ? <span className="header-notification-badge">{notificationCount > 99 ? "99+" : notificationCount}</span> : null}
              </Button>
              <Button className="header-user-button nav-reflect-button nav-reflect-button--profile" variant="secondary" size="md" onClick={() => onNavigate("/profilim")} title={user.email || ""}>
                {nickname || "Kullanıcı"}
              </Button>
            </div>
          </>
        ) : (
          <div className="header-auth-actions">
            <Button className="nav-reflect-button nav-reflect-button--login" variant="primary" size="md" onClick={() => onNavigate(appendReturnPath("/giris", returnPath))}>
              Giriş Yap
            </Button>
            <Button className="nav-reflect-button nav-reflect-button--signup" variant="secondary" size="md" onClick={() => onNavigate(appendReturnPath("/kayit", returnPath))}>
              Kayıt Ol
            </Button>
          </div>
        )}
      </nav>
    </header>
  );
}
